"use client";

import type { ReactNode } from "react";
import type { Key } from "react-aria-components";

import { useCallback, useMemo, useState } from "react";
import { Tabs, TabPanel } from "react-aria-components";

import { NativeAudioProvider } from "@/app/_providers/native-audio/NativeAudioProvider";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import type { ILinkIconSource } from "@ui/components/core/Link";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { STTFloatingCard } from "./STTFloatingCard";
import { STTHeroPanel } from "./STTHeroPanel";
import { STTUseCaseTabList } from "./STTUseCaseTabList";
import { useSTTHeroPlayback } from "./hooks/useSTTHeroPlayback";
import type { STTHeroSample, UploadFn } from "./hooks/useSTTHeroPlayback";

export interface STTHeroItem {
  title: string;
  descriptor: string;
  icon?: ReactNode;
  sample: STTHeroSample;
}

export interface STTHeroCta {
  label: string;
  href: string;
  variant: "primary" | "elevated";
  iconSource?: ILinkIconSource | null;
}

export interface STTHeroProps {
  /** Server-rendered static content (heading, subtitle, CTAs). Takes priority over title/subtitle/ctaLinks. */
  children?: ReactNode;
  title?: string;
  subtitle?: string | null;
  ctaLinks?: STTHeroCta[];
  backgroundImage?: React.ComponentProps<typeof STTHeroPanel>["backgroundImage"];
  items: STTHeroItem[];
  uploadFn?: UploadFn;
  allowUrlTranscription?: boolean;
}

interface STTHeroPlaybackPanelsProps {
  backgroundImage?: STTHeroProps["backgroundImage"];
  items: STTHeroItem[];
  samples: STTHeroSample[];
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  uploadFn?: UploadFn;
  allowUrlTranscription?: boolean;
}

function STTHeroPlaybackPanels({
  backgroundImage,
  items,
  samples,
  selectedIndex,
  setSelectedIndex,
  uploadFn,
  allowUrlTranscription = false,
}: STTHeroPlaybackPanelsProps) {
  const selectedItem = items[selectedIndex] ?? items[0];
  const {
    segments,
    isPlaying,
    isLoading,
    isUploading,
    uploadFileName,
    uploadedTranscription,
    uploadedThumbnailUrl,
    currentTime,
    togglePlay,
    seekToTime,
    handleFileChange,
    handleUrlSubmit,
    handleDownload,
    reportTranscriptionDownload,
    showUploadQuotaExceeded,
    uploadedMediaKind,
  } = useSTTHeroPlayback(samples, { selectedIndex, onSelectedIndexChange: setSelectedIndex, uploadFn });

  if (!selectedItem) return null;

  return (
    <div className='tw-relative tw-flex tw-items-center tw-justify-center'>
      <TabPanel id={selectedIndex} aria-label={selectedItem.title} className='tw-w-full'>
        <STTHeroPanel backgroundImage={backgroundImage}>
          <STTFloatingCard
            displayMode='videoPreview'
            sampleThumbnail={selectedItem.sample.thumbnail ?? null}
            uploadedMediaKind={uploadedMediaKind}
            sampleName={selectedItem.title}
            segments={segments}
            isLoading={isLoading}
            isPlaying={isPlaying}
            isUploading={isUploading}
            uploadFileName={uploadFileName}
            uploadedTranscription={uploadedTranscription}
            uploadedThumbnailUrl={uploadedThumbnailUrl}
            currentTime={currentTime}
            onTogglePlay={togglePlay}
            onWordClick={seekToTime}
            onFileChange={handleFileChange}
            onUrlSubmit={handleUrlSubmit}
            onDownload={handleDownload}
            onTranscriptionDownloaded={reportTranscriptionDownload}
            showLimitReachedOverlay={showUploadQuotaExceeded}
            allowUrlTranscription={allowUrlTranscription}
          />
        </STTHeroPanel>
      </TabPanel>
    </div>
  );
}

function STTHeroInner({
  children,
  title,
  subtitle,
  ctaLinks,
  backgroundImage,
  items,
  uploadFn,
  allowUrlTranscription = false,
}: STTHeroProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const samples = useMemo(() => items.map(item => item.sample), [items]);

  const handleSelectionChange = useCallback(
    (key: Key) => {
      if (typeof key !== "number") return;
      if (key !== selectedIndex) {
        captureEvent(EventName.mw_stt_example_clicked, { example_index: key });
      }
      setSelectedIndex(key);
    },
    [selectedIndex],
  );

  const header = useMemo(
    () =>
      children ?? (
        <div className='tw-flex tw-flex-col tw-gap-6'>
          <h2 className='tw-type-5xl tw-text-balance'>{title}</h2>
          {subtitle && <p className='tw-type-base tw-text-black/60'>{subtitle}</p>}
          {(ctaLinks?.length ?? 0) > 0 && (
            <div className='tw-flex tw-flex-wrap tw-gap-2 tw-mt-2'>
              {ctaLinks!.map((cta, idx) => (
                <LinkTracked
                  key={idx}
                  href={cta.href}
                  asButton
                  buttonVariants={{ variant: cta.variant, size: "lg" }}
                  iconSource={cta.iconSource}
                  eventName={
                    cta.variant === "primary" ? EventName.mw_hero_primary_link : EventName.mw_hero_secondary_link
                  }
                >
                  {cta.label}
                </LinkTracked>
              ))}
            </div>
          )}
        </div>
      ),
    [children, title, subtitle, ctaLinks],
  );

  return (
    <Tabs
      selectedKey={selectedIndex}
      onSelectionChange={handleSelectionChange}
      orientation='horizontal'
      className='tw-contents'
    >
      <div className='tw-grid tw-grid-cols-1 tw-gap-3 lg:tw-grid-cols-2 lg:tw-gap-12'>
        {/* Left column (top column on mobile): copy, CTAs, tab list */}
        <div className='tw-flex tw-flex-col tw-justify-start lg:tw-justify-between tw-pt-12 tw-gap-5 lg:tw-pb-9 tw-px-8'>
          {header}

          <STTUseCaseTabList items={items} selectedKey={selectedIndex} />
        </div>

        {/* Right column: atmospheric panel + floating card */}
        <STTHeroPlaybackPanels
          backgroundImage={backgroundImage}
          items={items}
          samples={samples}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          uploadFn={uploadFn}
          allowUrlTranscription={allowUrlTranscription}
        />
      </div>
    </Tabs>
  );
}

export function STTHero(props: STTHeroProps) {
  return (
    <NativeAudioProvider>
      <STTHeroInner {...props} />
    </NativeAudioProvider>
  );
}

export default STTHero;
